"use client";

import { SearchButtonBase } from "@/components/SearchButtonBase";
import { useSearchTriggerContext } from "@/contexts/SearchTriggerContext";
import { useApplyPendingSearch } from "@/hooks/useApplyPendingSearch";
import pendingSearch from "@/lib/pendingSearch";
import type React from "react";

type SearchButtonProps = {
  className?: string;
  /** Optional callback invoked after search is triggered */
  onClick?: (e?: React.MouseEvent | React.KeyboardEvent) => void;
  /** When true, always shows expanded state with text */
  forceExpanded?: boolean;
};

/**
 * Search button for homepage that updates current page URL with search filters
 */
export function SearchButton({
  className,
  onClick,
  forceExpanded,
}: SearchButtonProps) {
  const { apply, isPending } = useApplyPendingSearch();
  const { setTriggerOpen, setInitialTrigger } = useSearchTriggerContext();

  const handleSearch = (e?: React.MouseEvent | React.KeyboardEvent) => {
    const hasPendingChanges = pendingSearch.hasPendingChanges();

    // If no pending changes, open the activity dropdown instead of searching
    if (!hasPendingChanges) {
      e?.stopPropagation();
      setInitialTrigger("activity");
      setTriggerOpen("activities-dropdown", true);
      onClick?.(e);
      return;
    }

    // Otherwise, apply the search as normal
    apply(e);
    onClick?.(e);
  };

  return (
    <SearchButtonBase
      className={className}
      onSearch={handleSearch}
      isLoading={isPending}
      alwaysExpanded={forceExpanded}
    />
  );
}
